import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Controller, type Control, type FieldValues, type Path } from 'react-hook-form';

import { useSelectTypeAhead } from '@/hooks/useSelectTypeAhead';
import { BRAZIL_STATES } from '@/utils/data';

interface Props<TFieldValues extends FieldValues> {
  name: Path<TFieldValues>;
  control: Control<TFieldValues>;
  label: string;
  optionalLabel?: string;
  hint?: string;
  /** Label of an empty option, for an optional UF. Without it the field always takes a UF. */
  emptyLabel?: string;
}

/**
 * The UF select of every portal form: the 27 states by code, labelled like `FormTextField`, with
 * the field error underneath. Closed and focused, it answers to typing like a native select: "P"
 * then "E" picks PE; once open, the menu list searches by itself.
 */
const BrazilStateSelectField = <TFieldValues extends FieldValues>({
  name,
  control,
  label,
  optionalLabel,
  hint,
  emptyLabel,
}: Props<TFieldValues>) => {
  const typeAhead = useSelectTypeAhead();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const value = String(field.value ?? '');

        return (
          <FormControl fullWidth>
            <FormLabel htmlFor={name}>
              {label}
              {optionalLabel ? (
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={sx.optional}
                >
                  {optionalLabel}
                </Typography>
              ) : null}
            </FormLabel>
            <TextField
              {...field}
              value={value}
              id={name}
              select
              fullWidth
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message ?? hint}
              onKeyDown={typeAhead(BRAZIL_STATES, value, field.onChange)}
            >
              {emptyLabel !== undefined ? <MenuItem value="">{emptyLabel}</MenuItem> : null}
              {BRAZIL_STATES.map((state) => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
        );
      }}
    />
  );
};

export default BrazilStateSelectField;

const sx = {
  optional: { ml: 0.75 },
};
